import React, {FC, useEffect, useState} from "react";
import {Box, styled} from "@mui/material";
import {useSelector} from "react-redux";
import {AppStateType} from "../../../../../../../../../redux/reducers";
import {Document, Image, Page, PDFViewer, Text, View, StyleSheet, Font} from "@react-pdf/renderer";
import Loading from "../../../../../../../statistic/components/widgets/components/Loading";
import {ICompanyAPIKey} from "../../../../../../../../../models/CompanyAPIKey";
import CodeBlock from "./CodeBlock";
import AbsoluteLoading from "../../../../../../../../../components/AbsoluteLoading";
import {Alert} from "@material-ui/lab";

Font.register({
  family: 'BlinkMacSystemFont',
  fonts: [
    {
      src: `${process.env.PUBLIC_URL}/static/fonts/blinkmacsystemfont-light.ttf`,
      fontStyle: 'light'
    },
    {
      src: `${process.env.PUBLIC_URL}/static/fonts/blinkmacsystemfont-medium.ttf`,
      fontStyle: 'medium'
    },
    {
      src: `${process.env.PUBLIC_URL}/static/fonts/blinkmacsystemfont-medium.ttf`,
      fontStyle: 'regular'
    },
    {
      src: `${process.env.PUBLIC_URL}/static/fonts/blinkmacsystemfont-semibold.ttf`,
      fontStyle: 'semibold'
    },
  ]
});


Font.register({
  family: 'SourceCodePro',
  fonts: [
    {
      src: `${process.env.PUBLIC_URL}/static/fonts/SourceCodePro-Light.ttf`,
      fontStyle: 'light'
    },
    {
      src: `${process.env.PUBLIC_URL}/static/fonts/SourceCodePro-Regular.ttf`,
      fontStyle: 'regular'
    },
  ]
});

const styles = StyleSheet.create({
  medium: {
    fontFamily: 'BlinkMacSystemFont',
    fontStyle: 'medium',
    color: '#172b4d'
  },
  paragraph: {
    fontFamily: 'BlinkMacSystemFont',
    fontStyle: 'light',
    lineHeight: 1.714,
    color: '#172b4d',
    fontSize: 10,
  },
  block: {
    marginTop: 15
  }
})

type PropsType = {
  companyAPIKey?: ICompanyAPIKey
  name: string | undefined
}

const RegularInstruction: FC<PropsType> = ({
  companyAPIKey,
  name
}) => {
  const property = useSelector((state: AppStateType) => state.propertyReducer.property)!
  const [loading, setLoading] = useState(true)
  const [pdfRendered, setPdfRendered] = useState(false)

  useEffect(() => {
    if (pdfRendered && property) {
      setLoading(false)
    }
  }, [property, pdfRendered]);
  return (
    <Box position={'relative'}>
      <Alert variant={'filled'} style={{backgroundColor: '#323639'}}>This instruction is suitable for those web developers who have full access to the source code of the site.</Alert>
      <PDFViewer style={{ width: "100%", height: "100vh" }}>
        <Document onRender={() => setPdfRendered(true)}>
          <Page size="A4" wrap>
            <View style={{ position: "absolute", top: 15, right: 15 }}>
              <Image
                style={{
                  left: 0,
                  width: "auto",
                  height: "25px",
                  objectFit: "contain",
                }}
                src={`${process.env.PUBLIC_URL}/static/img/icons/HD-accessibility-logo-footer-2.0.png`}
              />
            </View>
            <View style={{ width: "100%", padding: '60px 50px 0 50px'}}>
              <Text style={{ fontSize: 15, ...styles.medium}}>
                {name} - Webmaster instructions
              </Text>
              <View style={styles.block}>
                <Text style={{fontSize: 12, ...styles.medium}}>Objective</Text>
                <Text style={{marginTop: 10, ...styles.paragraph}}>
                  The objective of the following document is to provide the necessary information and documentation on how to integrate the Handiscover Accessibility Widget into the dedicated customer website.
                </Text>
              </View>
              <View style={styles.block}>
                <Text style={{fontSize: 12, ...styles.medium}}>What is the Widget & How it works</Text>
                <View style={{flexDirection: "column", marginTop: 10}}>
                  <View style={{ flexDirection: "row", marginBottom: 4 }}>
                    <Text style={{ marginHorizontal: 8, ...styles.paragraph}}>•</Text>
                    <Text style={styles.paragraph}>The Accessibility Widget is a fast & quick way to show detailed accessibility data that has been previously logged into Handiscover’s
                      AMS System. This solution provides a web component/widget that can be inserted into the customer’s website.</Text>
                  </View>
                  <View style={{ flexDirection: "column", marginBottom: 4}}>
                    <View style={{ flexDirection: "row", marginBottom: 4}}>
                      <Text style={{ marginHorizontal: 8, ...styles.paragraph }}>•</Text>
                      <Text style={{fontSize: 10, ...styles.medium}}>AMS Widget Architecture:</Text>
                    </View>
                    <View style={{ flexDirection: 'row', marginBottom: 4, marginLeft: 14}}>
                      <Text style={{ marginHorizontal: 8, ...styles.paragraph }}>•</Text>
                      <Text style={styles.paragraph}>Javascript function that inserts the widget on the selected page that will be responsible for all the rendering associated with the widget.</Text>
                    </View>
                    <View style={{ flexDirection: 'row', marginBottom: 4, marginLeft: 14}}>
                      <Text style={{ marginHorizontal: 8, ...styles.paragraph }}>•</Text>
                      <Text style={styles.paragraph}>All the data is coming from Handiscover API.</Text>
                    </View>
                    <View style={{ flexDirection: 'row', marginBottom: 4, marginLeft: 14}}>
                      <Text style={{ marginHorizontal: 8, ...styles.paragraph }}>•</Text>
                      <Text style={styles.paragraph}>This Javascript function accepts two parameters:</Text>
                    </View>
                    <View style={{ flexDirection: 'row', marginBottom: 4, marginLeft: 30}}>
                      <Text style={{ marginHorizontal: 8, ...styles.paragraph }}>•</Text>
                      <Text style={styles.paragraph}><Text style={styles.medium}>company_id:</Text> Handiscover’s id for the customer company, for security reasons there is the need to differ all the companies so there is no data breach from other companies.</Text>
                    </View>
                    <View style={{ flexDirection: 'row', marginBottom: 4, marginLeft: 30}}>
                      <Text style={{ marginHorizontal: 8, ...styles.paragraph }}>•</Text>
                      <Text style={styles.paragraph}><Text style={styles.medium}>property_id:</Text> Id for selected property, this allows to only get data on the provided property</Text>
                    </View>
                    <View style={{ flexDirection: "row", marginBottom: 4}}>
                      <Text style={{ marginHorizontal: 8, ...styles.paragraph }}>•</Text>
                      <Text style={{fontSize: 10, ...styles.medium}}>How to insert the widget?</Text>
                    </View>
                    <View style={{ flexDirection: 'row', marginBottom: 4, marginLeft: 14}}>
                      <Text style={{ marginHorizontal: 8, ...styles.paragraph }}>•</Text>
                      <Text style={styles.paragraph}>Insert the following HTML lines on your page in tag <Text style={styles.medium}>{'<body/>'}</Text>:</Text>
                    </View>
                    <View style={{marginLeft: 22, marginBottom: 4}}>
                      <CodeBlock propertyId={property.id} companyAPIKey={companyAPIKey && companyAPIKey.key ? companyAPIKey.key : ''}/>
                    </View>
                    <View style={{ flexDirection: 'row', marginBottom: 4, marginLeft: 14}}>
                      <Text style={{ marginHorizontal: 8, ...styles.paragraph }}>•</Text>
                      <Text style={styles.paragraph}>This will load all the necessary resources for the widget to render.</Text>
                    </View>
                  </View>
                </View>
              </View>
              <View style={styles.block}>
                <Text style={{fontSize: 12, ...styles.medium}}>User interaction and design</Text>
                <Text style={{marginVertical: 10, ...styles.paragraph}}>How to preview/test the widget:</Text>
                <View style={{flexDirection: "column"}}>
                  <View style={{ flexDirection: "row", marginBottom: 4 }}>
                    <Text style={{ marginHorizontal: 8, ...styles.paragraph}}>1.</Text>
                    <Text style={styles.paragraph}>After adding the script to the main page of the website and deploying it to your live environment the next time the webpage is loaded
                      there should be an icon like this:</Text>
                  </View>
                  <View style={{alignItems: 'center', justifyContent: 'center', marginBottom: 4}}>
                    <Image src={'/static/img/other/accessibility-button.png'} style={{ width: 120, objectFit: 'contain'}}/>
                  </View>
                  <View style={{ flexDirection: "row", marginBottom: 4 }}>
                    <Text style={{ marginHorizontal: 8, ...styles.paragraph}}>2.</Text>
                    <Text style={styles.paragraph}>This means the widget has been place successfully on the website and it’s loading all the accessibility data to present on the widget.</Text>
                  </View>
                  <View style={{ flexDirection: "row", marginBottom: 4 }}>
                    <Text style={{ marginHorizontal: 8, ...styles.paragraph}}>3.</Text>
                    <Text style={styles.paragraph}>From this point on the widget has been successfully loaded and it’s accessible by clicking on it.</Text>
                  </View>
                  <View style={{ flexDirection: "row", marginBottom: 4 }}>
                    <Text style={{ marginHorizontal: 8, ...styles.paragraph}}>4.</Text>
                    <Text style={styles.paragraph}>After clicking on the button the widget will appear (like the beneath example).</Text>
                  </View>
                </View>
              </View>
            </View>
          </Page>
        </Document>
      </PDFViewer>
      <AbsoluteLoading visible={loading}/>
    </Box>
  )
}

export default RegularInstruction